import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { finalize, tap } from 'rxjs';
import { USE_ADMIN_API } from '../../core/interceptors/api.context';
import { AuthResponse, AuthUserType, CurrentUserResponse, LoginRequest, User } from '../../models/auth.types';


@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

    readonly isSubmitting = signal(false);
  readonly currentUser = signal<User | null>(this.getStoredUser());
  readonly userProfile = signal<User | null>(null);
  readonly userType = signal<AuthUserType | null>(this.getStoredUserType());
  /**
   * Register company owner/customer using Admin API.
   *
   * Change the endpoint according to your Admin backend route.
   */
  registerCustomer(payload: unknown) {
    this.isSubmitting.set(true);

    return this.http.post('/api/v1/customers/register', payload, {
      context: new HttpContext().set(USE_ADMIN_API, true),
    }).pipe(
      finalize(() => this.isSubmitting.set(false)),
    );
  }

  /**
   * Login using the default DMS API.
   */
  login(credentials: LoginRequest) {
  this.isSubmitting.set(true);

  return this.http.post<AuthResponse>('/api/v1/login', credentials).pipe(
    tap((response) => {
      if (!response.success) {
        return;
      }

      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('currentUser', JSON.stringify(response.data.user));
      localStorage.setItem('userType', response.user_type);

      this.currentUser.set(response.data.user);
      this.userProfile.set(response.data.user);
      this.userType.set(response.user_type);
    }),
    finalize(() => {
      this.isSubmitting.set(false);
    }),
  );
}

  /**
   * Get logged-in user details from DMS API.
   */
  getUserDetails() {
    return this.http.get<CurrentUserResponse>('/api/v1/current-user').pipe(
      tap((response) => {
        if (!response.success || !response.data?.user) {
          return;
        }

        const user = response.data.user;

        this.currentUser.set(user);
        this.userProfile.set(user);
        this.userType.set(response.user_type);

        localStorage.setItem('currentUser', JSON.stringify(user));
        localStorage.setItem('userType', response.user_type);
      }),
    );
  }


  /**
   * Check whether a token is available.
   */
  isLoggedIn(): boolean {
    return Boolean(localStorage.getItem('authToken'));
  }

  /**
   * Return the stored authentication token.
   */
  getToken(): string | null {
    return localStorage.getItem('authToken');
  }

  getUserType(): AuthUserType | null {
    return this.userType();
  }

  /**
   * Clear authentication data and redirect to login.
   */
  logout(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('userType');

    this.currentUser.set(null);
    this.userProfile.set(null);
    this.userType.set(null);

    void this.router.navigate(['/login']);
  }


  private getStoredUser(): User | null {
    const storedUser = localStorage.getItem('currentUser');

    if (!storedUser) {
      return null;
    }

    try {
      return JSON.parse(storedUser) as User;
    } catch {
      localStorage.removeItem('currentUser');
      return null;
    }
  }
   private getStoredUserType(): AuthUserType | null {
    const storedUserType = localStorage.getItem('userType');

    if (storedUserType === 'admin' || storedUserType === 'user') {
      return storedUserType;
    }

    return null;
  }
}